import React from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronDown } from 'lucide-react';
import {
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  Tooltip as RadixTooltip,
  TooltipProvider,
  TooltipTrigger,
  TooltipContent,
} from '@cargomatic/ui';
import {
  accessorialPaid,
  accessorialProcessed,
  userCanApproveAccessorials as userCanApproveAccessorialsHelper,
  checkIfEquipmentIsMissingForAccessorial,
  checkWhichSideAccessorialAutoDeclined,
  isCarrierMissingInAccessorial,
} from 'helpers/accessorials';
import { isShipmentCanceled } from 'helpers/shipments';
import SessionStore from 'stores/SessionStore';
import { TOOLTIP_CONTENT } from './accessorialTooltips';

const BulkActionsMenu = ({
  accessorial,
  shipment,
  isAccessorialsAdmin,
  isEditSaving,
  isApproveSaving,
  isDeclineSaving,
  onBulkAction,
}) => {
  const { t } = useTranslation();
  const isAnySaving = isEditSaving || isApproveSaving || isDeclineSaving;
  const isProcessed = accessorialProcessed(shipment, accessorial);

  const shipmentCarrier = shipment?.carrier?._id || shipment?.carrier;
  const carrierAssociatedWithAccessorial =
    accessorial.carrier?._id || accessorial.carrier;
  const carrierMismatch =
    accessorial.payee_type === 'carrier' &&
    shipmentCarrier !== carrierAssociatedWithAccessorial;

  const isAlreadyApproved =
    accessorial.carrier_status === 'approved' &&
    accessorial.shipper_status === 'approved';

  const disableBulkApprove = carrierMismatch || isAlreadyApproved;
  const disablePayeeResolutions = disableBulkApprove;

  const isMissingDocs =
    !!accessorial?.missing_docs ||
    accessorial?.customer_approval_provided === false;

  const isMissingEquipment =
    checkIfEquipmentIsMissingForAccessorial(accessorial);
  const shipperSideDeclined = checkWhichSideAccessorialAutoDeclined(
    accessorial,
    'shipper',
  );
  const carrierSideDeclined = checkWhichSideAccessorialAutoDeclined(
    accessorial,
    'carrier',
  );
  const isCarrierMissing = isCarrierMissingInAccessorial(accessorial);
  const accessorialDeclined = shipperSideDeclined || carrierSideDeclined;

  const payableIsProcessed = accessorialPaid(shipment, accessorial);
  const canUserApprove = () => {
    if (isMissingEquipment || accessorialDeclined || isCarrierMissing) {
      return false;
    }
    const user = SessionStore.getUser();
    const canSelect = userCanApproveAccessorialsHelper(user, [accessorial]);
    if (canSelect && isAccessorialsAdmin) {
      return true;
    }
    return canSelect && !isMissingDocs;
  };

  let approveTooltipContent = TOOLTIP_CONTENT.MISSING_DOCS;
  if (isMissingEquipment)
    approveTooltipContent = TOOLTIP_CONTENT.MISSING_EQUIPMENT;
  if (isCarrierMissing)
    approveTooltipContent = TOOLTIP_CONTENT.NO_CARRIER_ASSIGNED_INFO;
  if (accessorialDeclined)
    approveTooltipContent = TOOLTIP_CONTENT.SHIPPER_CHANGED;

  const disableDeclineBtn =
    accessorialDeclined ||
    isCarrierMissing ||
    (carrierMismatch && payableIsProcessed);
  const declineTooltipContent = accessorialDeclined
    ? TOOLTIP_CONTENT.SHIPPER_CHANGED
    : TOOLTIP_CONTENT.NO_CARRIER_ASSIGNED_INFO;

  const disabled =
    isShipmentCanceled(shipment) ||
    isProcessed ||
    disablePayeeResolutions ||
    isAnySaving;

  const renderApproveItem = () => {
    const approveDisabled = !canUserApprove();
    const item = (
      <DropdownMenuItem
        disabled={approveDisabled}
        onSelect={() => !approveDisabled && onBulkAction('approve-all')}
      >
        {t('APPROVE_ALL')}
      </DropdownMenuItem>
    );
    if (approveDisabled) {
      return (
        <TooltipProvider>
          <RadixTooltip>
            <TooltipTrigger asChild>
              <span>{item}</span>
            </TooltipTrigger>
            <TooltipContent side="left">{approveTooltipContent}</TooltipContent>
          </RadixTooltip>
        </TooltipProvider>
      );
    }
    return item;
  };

  const renderDeclineItem = () => {
    const item = (
      <DropdownMenuItem
        disabled={disableDeclineBtn}
        onSelect={() => !disableDeclineBtn && onBulkAction('decline-all')}
      >
        {t('DECLINE_ALL')}
      </DropdownMenuItem>
    );
    if (disableDeclineBtn) {
      return (
        <TooltipProvider>
          <RadixTooltip>
            <TooltipTrigger asChild>
              <span>{item}</span>
            </TooltipTrigger>
            <TooltipContent side="left">{declineTooltipContent}</TooltipContent>
          </RadixTooltip>
        </TooltipProvider>
      );
    }
    return item;
  };

  const renderDropdown = () => (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          data-testid="dropdown-toggle"
          variant="outline"
          size="sm"
          disabled={disabled}
          className="gap-1 border-green-cm text-green-cm hover:bg-green-50"
        >
          {t('ACTIONS')}
          <ChevronDown className="h-4 w-4" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        {renderApproveItem()}
        {renderDeclineItem()}
      </DropdownMenuContent>
    </DropdownMenu>
  );

  if (isProcessed) {
    return (
      <TooltipProvider>
        <RadixTooltip>
          <TooltipTrigger asChild>
            <span data-testid="span-1c69101c">{renderDropdown()}</span>
          </TooltipTrigger>
          <TooltipContent side="bottom">
            {TOOLTIP_CONTENT.PROCESSED}
          </TooltipContent>
        </RadixTooltip>
      </TooltipProvider>
    );
  }
  return renderDropdown();
};

export default BulkActionsMenu;
